import { Button, Divider, Dropdown, Form, Input, InputNumber, MenuProps, Modal, Radio, RadioChangeEvent, Select, Space, Spin, Tooltip, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import ILoan from '~Api/Loan/ILoan';
import ILoanFee from '~Api/Loan/ILoanFee';
import ILoanPayoutFigure from '~Api/Loan/ILoanPayoutFigure';
import ILoanPayoutFigureItem from '~Api/Loan/ILoanPayoutFigureItem';
import ILoanPayoutFigureSection from '~Api/Loan/ILoanPayoutFigureSection';
import LoanPayoutFigureApprovalStatusEnum from '~Api/Loan/LoanPayoutFigureApprovalStatusEnum';
import LoanPayoutFigureSectionTypeEnum from '~Api/Loan/LoanPayoutFigureSectionTypeEnum';
import {
    loanFeesListAction,
    loanGetAction,
    loanPayoutFigureCalculationAction,
    loanPayoutFiguresAddAction,
    loanTransactionsListAction,
} from '~Loans/actions';
import {
    loanFeesSelector,
    loanPayoutFigureCalculationSelector,
    loanSelector,
    loanTransactionsSelector,
} from '~Loans/selectors';
import {
    getLoanAppliedAccruedInterestTotal,
    getLoanPayoutFigureTotals,
} from '~Loans/utilities';
import { IGlobalState } from '~reducer';
import DatePicker from '~UI/DatePicker';
import { IDictionary } from '~utilities/IDictionary';
import Layout from './Layout';
import { currencyFormatter } from '~utilities/formatters';
import LoanPayoutFigureAccruedInterestModeEnum from '~Api/Loan/LoanPayoutFigureAccruedInterestModeEnum';
import LoanPayoutFigurePrepaidInterestModeEnum from '~Api/Loan/LoanPayoutFigurePrepaidInterestModeEnum';
import { DownOutlined, WarningOutlined } from '@ant-design/icons';
import PayoutFigureInterestCalculatorModal from './PayoutFigureInterestCalculatorModal';
import LoanPayoutFigureMinimumTermInterestModeEnum from '~Api/Loan/LoanPayoutFigureMinimumTermInterestModeEnum';
import DischargeInterestTypeEnum from '~Api/Application/DischargeInterestTypeEnum';
import { applicationValueSetAction } from '~Applications/actions';
import InterestMethodEnum from '~Api/Loan/InterestMethodEnum';
import ILoanPayoutFigureCalculation from '~Api/Loan/ILoanPayoutFigureCalculation';
import ILoanTransaction from '~Api/Loan/ILoanTransaction';

const defaultSections: LoanPayoutFigureSectionTypeEnum[] = [
    LoanPayoutFigureSectionTypeEnum.Principal,
    LoanPayoutFigureSectionTypeEnum.Interest,
    LoanPayoutFigureSectionTypeEnum.Other,
    LoanPayoutFigureSectionTypeEnum.Renewal,
    LoanPayoutFigureSectionTypeEnum.DefaultFees,
];

const sectionLabels: IDictionary<string> = {
    [LoanPayoutFigureSectionTypeEnum.Principal]: 'Principal',
    [LoanPayoutFigureSectionTypeEnum.Interest]: 'Interest',
    [LoanPayoutFigureSectionTypeEnum.Other]: 'Other (Inc GST)',
    [LoanPayoutFigureSectionTypeEnum.Renewal]: 'Renewal/Variation',
    [LoanPayoutFigureSectionTypeEnum.DefaultFees]: 'Default Fees',
};

interface ISection {
    lineItems: IDictionary<ILineItem>;
    name: string;
    type: LoanPayoutFigureSectionTypeEnum;
}

interface ILineItem {
    amount: number;
    code: string;
    name: string;
}

export default function PayoutFigureAdd(): ReactElement {
    const { uuid } = useParams<{ uuid: string }>();

    const fees: ILoanFee[] = useSelector((state: IGlobalState): ILoanFee[] => loanFeesSelector(state, uuid));
    const loan: ILoan = useSelector((state: IGlobalState): ILoan => loanSelector(state, uuid));
    const loanPayoutFigureCalculations: IDictionary<ILoanPayoutFigureCalculation> = useSelector((state: IGlobalState): IDictionary<ILoanPayoutFigureCalculation> => loanPayoutFigureCalculationSelector(state, uuid));
    const transactions: IDictionary<ILoanTransaction> = useSelector((state: IGlobalState): IDictionary<ILoanTransaction> => loanTransactionsSelector(state, uuid));

    const [ accruedInterestMode, setAccruedInterestMode ] = useState<LoanPayoutFigureAccruedInterestModeEnum>(LoanPayoutFigureAccruedInterestModeEnum.Auto);
    const [ borrowerName, setBorrowerName ] = useState<string>(null);
    const [ dischargeDate, setDischargeDate ] = useState<string>(null);
    const [ dischargeInterestType, setDischargeInterestType ] = useState<string>(null);
    const [ errors, setErrors ] = useState<IDictionary<string>>({});
    const [ isInterestCalculatorModalOpen, setIsInterestCalculatorModalOpen ] = useState<boolean>(false);
    const [ isMissingDetailsModalOpen, setIsMissingDetailsModalOpen ] = useState<boolean>(true);
    const [ loanCode, setLoanCode ] = useState<string>(null);
    const [ minimumTermInterestMode, setMinimumTermInterestMode ] = useState<LoanPayoutFigureMinimumTermInterestModeEnum>(LoanPayoutFigureMinimumTermInterestModeEnum.Auto);
    const [ prepaidInterest, setPrepaidInterest ] = useState<number>(null);
    const [ prepaidInterestMode, setPrepaidInterestMode ] = useState<LoanPayoutFigurePrepaidInterestModeEnum>(LoanPayoutFigurePrepaidInterestModeEnum.Auto);
    const [ sections, setSections ] = useState<IDictionary<ISection>>(null);
    const [ trustAmount, setTrustAmount ] = useState<number>(null);

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!fees) {
            dispatch(loanFeesListAction(uuid));
        }
    }, [
        fees,
        uuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!loan) {
            dispatch(loanGetAction(uuid));
        }
    }, [
        loan,
        uuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!transactions) {
            dispatch(loanTransactionsListAction(uuid));
        }
    }, [
        transactions,
        uuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!sections && loan && fees && transactions) {
            const feeLineItems: IDictionary<ILineItem> = {};
            fees.forEach((fee: ILoanFee): void => {
                feeLineItems[`FEE_${fee.uuid}`] = {
                    amount: fee.amount,
                    code: `FEE_${fee.uuid}`,
                    name: fee.description,
                };
            });

            const principalLineItems: IDictionary<ILineItem> = {};
            _.each(transactions, (transaction: ILoanTransaction): void => {
                if (transaction.amountPrincipal !== 0) {
                    principalLineItems[transaction.uuid] = {
                        // Figures on Payout figure are inverted to transaction.
                        amount: transaction.amountPrincipal * -1,
                        code: transaction.uuid,
                        name: `${dayjs(transaction.transactionTime).format('DD/MM/YYYY')} - ${transaction.description}`,
                    };
                }
            });

            setSections({
                [LoanPayoutFigureSectionTypeEnum.Principal]: {
                    lineItems: principalLineItems,
                    name: 'Principal',
                    type: LoanPayoutFigureSectionTypeEnum.Principal,
                },
                [LoanPayoutFigureSectionTypeEnum.Interest]: {
                    // Do not include the default filled interest field if we can calculate the interest automatically
                    lineItems: loan.currentPaymentDate ? {} : {
                        ['INTEREST_INTEREST_CHARGED']: {
                            amount: null,
                            code: 'INTEREST_INTEREST_CHARGED',
                            name: 'Interest Charged',
                        },
                    },
                    name: 'Interest',
                    type: LoanPayoutFigureSectionTypeEnum.Interest,
                },
                [LoanPayoutFigureSectionTypeEnum.Other]: {
                    lineItems: {
                        ['OTHER_LENDERS_DISCHARGE_FEE']: {
                            amount: null,
                            code: 'OTHER_LENDERS_DISCHARGE_FEE',
                            name: 'Lender\'s Discharge Fee',
                        },
                        // eslint-disable-next-line sort-keys
                        ['OTHER_LAWYERS_DISCHARGE_FEE']: {
                            amount: null,
                            code: 'OTHER_LAWYERS_DISCHARGE_FEE',
                            name: 'Lawyer\'s Discharge Fee & Outlays',
                        },
                        ['OTHER_LENDERS_OUTLAYS']: {
                            amount: null,
                            code: 'OTHER_LENDERS_OUTLAYS',
                            name: 'Lender\'s Outlays',
                        },
                        // eslint-disable-next-line sort-keys
                        ['OTHER_FAILED_SETTLEMENT_FEES']: {
                            amount: null,
                            code: 'OTHER_FAILED_SETTLEMENT_FEES',
                            name: 'Failed Settlement Fees',
                        },
                        ['OTHER_SETTLEMENT_FEE']: {
                            amount: null,
                            code: 'OTHER_SETTLEMENT_FEE',
                            name: '30 Days Not / Urgent Settlement Fee',
                        },
                        ...feeLineItems,
                    },
                    name: 'Other (Inc GST)',
                    type: LoanPayoutFigureSectionTypeEnum.Other,
                },
                [LoanPayoutFigureSectionTypeEnum.Renewal]: {
                    lineItems: {
                        ['RENEWAL_OUTSTANDING_FEE']: {
                            amount: null,
                            code: 'RENEWAL_OUTSTANDING_FEE',
                            name: 'Outstanding Renewal Fee',
                        },
                        // eslint-disable-next-line sort-keys
                        ['RENEWAL_OUTSTANDING_DOC_FEE']: {
                            amount: null,
                            code: 'RENEWAL_OUTSTANDING_DOC_FEE',
                            name: 'Outstanding Renewal Doc Fee',
                        },
                    },
                    name: 'Renewal/Variation',
                    type: LoanPayoutFigureSectionTypeEnum.Renewal,
                },
                [LoanPayoutFigureSectionTypeEnum.DefaultFees]: {
                    lineItems: {
                        ['DEFAULT_NOTICE_FEE']: {
                            amount: null,
                            code: 'DEFAULT_NOTICE_FEE',
                            name: 'Default Notice Fee',
                        },
                        // eslint-disable-next-line sort-keys
                        ['DEFAULT_MONTHLY_ADMIN_FEES']: {
                            amount: null,
                            code: 'DEFAULT_MONTHLY_ADMIN_FEES',
                            name: 'X Monthly Default Admin Fees',
                        },
                        // eslint-disable-next-line sort-keys
                        ['DEFAULT_LAWYERS_COSTS']: {
                            amount: null,
                            code: 'DEFAULT_LAWYERS_COSTS',
                            name: 'Lawyer\'s Default Costs',
                        },
                        ['DEFAULT_LENDERS_OUTLAYS']: {
                            amount: null,
                            code: 'DEFAULT_LENDERS_OUTLAYS',
                            name: 'Lender\'s Outlays (Court Filing Costs, Agent Fees etc.)',
                        },
                    },
                    name: 'Default Fees',
                    type: LoanPayoutFigureSectionTypeEnum.DefaultFees,
                },
            });
        }
    }, [
        fees,
        loan,
        sections,
        transactions,
    ]);

    /**
     * Disables dates before the current payment date for the loan
     */
    const disabledDate: (date: Dayjs) => boolean = useCallback((date: Dayjs): boolean => {
        return date && loan.currentPaymentDate && date.format('YYYY-MM-DD') < loan.currentPaymentDate;
    }, [
        loan,
    ]);

    const appliedPrepaidInterestMode: LoanPayoutFigurePrepaidInterestModeEnum = (
        (loan && loan.currentPaymentDate)
        ? prepaidInterestMode
        : LoanPayoutFigurePrepaidInterestModeEnum.Manual
    );
    const appliedAccruedInterestMode: LoanPayoutFigureAccruedInterestModeEnum = (
        (loan && loan.currentPaymentDate)
        ? accruedInterestMode
        : LoanPayoutFigureAccruedInterestModeEnum.Off
    );
    const appliedMinimumTermInterestMode: LoanPayoutFigureMinimumTermInterestModeEnum = (
        (loan && loan.currentPaymentDate && loan.application.dischargeInterestType === DischargeInterestTypeEnum.MinimumTerm)
        ? minimumTermInterestMode
        : LoanPayoutFigureMinimumTermInterestModeEnum.Off
    );

    /**
     * Simulates a payout figure object used for interest calculations
     */
    const loanPayoutFigure: ILoanPayoutFigure = useMemo((): ILoanPayoutFigure => {
        if (!loan) {
            return;
        }

        const payoutFigureCalculation: ILoanPayoutFigureCalculation = loanPayoutFigureCalculations && loanPayoutFigureCalculations[dischargeDate];

        return {
            accruedInterest: payoutFigureCalculation && payoutFigureCalculation.accruedInterest,
            accruedInterestLabel: payoutFigureCalculation && payoutFigureCalculation.accruedInterestLabel,
            accruedInterestMode: appliedAccruedInterestMode,
            approvalStatus: LoanPayoutFigureApprovalStatusEnum.Draft,
            borrowerName: borrowerName || loan.contactName,
            dischargeDate,
            loanCode: loanCode || loan.code || loan.salesforceCode,
            loanUuid: loan.uuid,
            minimumTermInterest: payoutFigureCalculation && payoutFigureCalculation.minimumTermInterest,
            minimumTermInterestMode: appliedMinimumTermInterestMode,
            prepaidInterest,
            prepaidInterestAuto: payoutFigureCalculation && payoutFigureCalculation.prepaidInterest,
            prepaidInterestMode: appliedPrepaidInterestMode,
            sections: _.map(sections, (section: ISection): ILoanPayoutFigureSection => ({
                items: _.map(section.lineItems, (lineItem: ILineItem): ILoanPayoutFigureItem => ({
                    amount: lineItem.amount,
                    name: lineItem.name,
                })),
                name: section.name,
                type: section.type,
            })),
            trustAmount,
        };
    }, [
        appliedAccruedInterestMode,
        appliedMinimumTermInterestMode,
        appliedPrepaidInterestMode,
        borrowerName,
        dischargeDate,
        loan,
        loanCode,
        loanPayoutFigureCalculations,
        prepaidInterest,
        sections,
        trustAmount,
    ]);

    const onClickAddLineItem: (sectionCode: string) => void = useCallback((sectionCode: string): void => {
        const code: string = dayjs().format();

        setSections((prevState: IDictionary<ISection>): IDictionary<ISection> => ({
            ...prevState,
            [sectionCode]: {
                ...prevState[sectionCode],
                lineItems: {
                    ...prevState[sectionCode].lineItems,
                    [code]: {
                        amount: null,
                        code,
                        name: null,
                    },
                },
            },
        }));
    }, []);

    const onClickDeleteLineItem: (sectionCode: string, lineItemCode: string) => void = useCallback((sectionCode: string, lineItemCode: string): void => {
        Modal.confirm({
            okText: 'Delete',
            onOk: (): void => {
                setSections((prevState: IDictionary<ISection>): IDictionary<ISection> => ({
                    ...prevState,
                    [sectionCode]: {
                        ...prevState[sectionCode],
                        lineItems: _.omit(prevState[sectionCode].lineItems, [ lineItemCode ]),
                    },
                }));
            },
            title: 'Delete Line Item',
        });
    }, []);

    const onClickMissingDetailsCancel: () => void = useCallback((): void => {
        setIsMissingDetailsModalOpen(false);
    }, []);

    const onClickMissingDetailsOk: () => void = useCallback((): void => {
        dispatch(applicationValueSetAction(loan.applicationUuid, 'dischargeInterestType', dischargeInterestType));

        onClickMissingDetailsCancel();
    }, [
        dischargeInterestType,
        dispatch,
        loan,
        onClickMissingDetailsCancel,
    ]);

    const onCloseInterestCalculatorModal: () => void = useCallback((): void => {
        setIsInterestCalculatorModalOpen(false);
    }, []);

    const onClickAutoCalculate: () => void = useCallback((): void => {
        if (!dischargeDate) {
            Modal.error({
                content: 'Please select a proposed payout date',
                title: 'Unable to Auto Calculate',
            });

            return;
        }

        setIsInterestCalculatorModalOpen(true);
    }, [
        dischargeDate,
    ]);

    const recalculateInterest: (dischargeDate: string) => void = useCallback((dischargeDate: string): void => {
        // There is no need to do interest calculations if there is no loan or a loan current payment date
        if (!loan || !loan.currentPaymentDate) {
            return;
        }

        if (!dischargeDate) {
            return;
        }

        if (!loanPayoutFigureCalculations || !loanPayoutFigureCalculations[dischargeDate]) {
            dispatch(loanPayoutFigureCalculationAction(uuid, dischargeDate));
        }
    }, [
        loan,
        loanPayoutFigureCalculations,
        dispatch,
        uuid,
    ]);

    const switchToPrepaidInterestAuto: () => void = useCallback((): void => {
        setPrepaidInterestMode(LoanPayoutFigurePrepaidInterestModeEnum.Auto);
    }, []);

    const switchToPrepaidInterestManual: () => void = useCallback((): void => {
        setPrepaidInterestMode(LoanPayoutFigurePrepaidInterestModeEnum.Manual);
    }, []);

    const validateDischargeDate: (errors: IDictionary<string>) => boolean = useCallback((errors: IDictionary<string>): boolean => {
        let error: string;

        if (!dischargeDate) {
            error = 'Please enter the proposed payout date';
        }

        errors.dischargeDate = error;

        setErrors(errors);

        return !error;
    }, [
        dischargeDate,
        setErrors,
    ]);

    const validateLineItemAmount: (lineItem: ILineItem, errors: IDictionary<string>) => boolean = useCallback((lineItem: ILineItem, errors: IDictionary<string>): boolean => {
        let error: string;

        if (null === lineItem.amount) {
            error = 'Please enter the amount';
        }

        errors[`lineItem_${lineItem.code}_amount`] = error;

        setErrors(errors);

        return !error;
    }, []);

    const validateLineItemName: (lineItem: ILineItem, errors: IDictionary<string>) => boolean = useCallback((lineItem: ILineItem, errors: IDictionary<string>): boolean => {
        let error: string;

        if (!lineItem.name) {
            error = 'Please enter the label';
        }

        errors[`lineItem_${lineItem.code}_name`] = error;

        setErrors(errors);

        return !error;
    }, []);

    const validatePrepaidInterest: (errors: IDictionary<string>) => boolean = useCallback((errors: IDictionary<string>): boolean => {
        let error: string;

        if (null === prepaidInterest) {
            error = 'Please enter the remaining prepaid interest';
        }

        errors.prepaidInterest = error;

        setErrors(errors);

        return !error;
    }, [
        prepaidInterest,
    ]);

    const onChangeLineItemAmount: (sectionCode: string, lineItemCode: string, value: number) => void = useCallback((sectionCode: string, lineItemCode: string, value: number): void => {
        setSections((prevState: IDictionary<ISection>): IDictionary<ISection> => ({
            ...prevState,
            [sectionCode]: {
                ...prevState[sectionCode],
                lineItems: {
                    ...prevState[sectionCode].lineItems,
                    [lineItemCode]: {
                        ...prevState[sectionCode].lineItems[lineItemCode],
                        amount: value,
                    },
                },
            },
        }));
    }, []);

    const onChangeLineItemName: (sectionCode: string, lineItemCode: string, event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((sectionCode: string, lineItemCode: string, event: React.ChangeEvent<HTMLInputElement>): void => {
        // Keep access to event after event handler has run. See https://legacy.reactjs.org/docs/legacy-event-pooling.html
        event.persist();

        setSections((prevState: IDictionary<ISection>): IDictionary<ISection> => ({
            ...prevState,
            [sectionCode]: {
                ...prevState[sectionCode],
                lineItems: {
                    ...prevState[sectionCode].lineItems,
                    [lineItemCode]: {
                        ...prevState[sectionCode].lineItems[lineItemCode],
                        name: event.target.value,
                    },
                },
            },
        }));
    }, []);

    const onClickAddSection: (sectionCode: LoanPayoutFigureSectionTypeEnum) => void = useCallback((sectionCode: LoanPayoutFigureSectionTypeEnum): void => {
        setSections((prevState: IDictionary<ISection>): IDictionary<ISection> => ({
            ...prevState,
            [sectionCode]: {
                lineItems: {},
                name: sectionLabels[sectionCode],
                type: sectionCode,
            },
        }));
    }, []);

    const onClickDeleteSection: (sectionCode: string) => void = useCallback((sectionCode: string): void => {
        setSections((prevState: IDictionary<ISection>): IDictionary<ISection> => (_.omit(prevState, [ sectionCode ])));
    }, []);

    const onChangeBorrowerName: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        setBorrowerName(event.target.value);
    }, []);

    const onChangeDischargeDate: (date: Dayjs) => void = useCallback((date: Dayjs): void => {
        const dischargeDate: string = date ? date.format('YYYY-MM-DD') : null;

        setDischargeDate(dischargeDate);

        recalculateInterest(dischargeDate);
    }, [
        recalculateInterest,
    ]);

    const onChangeLoanCode: (event: RadioChangeEvent) => void = useCallback((event: RadioChangeEvent): void => {
        setLoanCode(event.target.value);
    }, []);

    const onChangeTrustAmount: (value: number) => void = useCallback((value: number): void => {
        setTrustAmount(value);
    }, []);

    const onChangePrepaidInterest: (value: number) => void = useCallback((value: number): void => {
        setPrepaidInterest(value || null);
    }, []);

    const onChangeDischargeInterestType: (dischargeInterestType: DischargeInterestTypeEnum) => void = useCallback((dischargeInterestType: DischargeInterestTypeEnum): void => {
        setDischargeInterestType(dischargeInterestType);
    }, []);

    const onClickCreate: () => void = useCallback((): void => {
        let valid: boolean = true;
        const errors: IDictionary<string> = {};

        valid = validateDischargeDate(errors) && valid;

        if (appliedPrepaidInterestMode === LoanPayoutFigurePrepaidInterestModeEnum.Manual) {
            valid = validatePrepaidInterest(errors) && valid;
        }

        _.each(sections, (section: ISection): void => {
            _.each(section.lineItems, (lineItem: ILineItem): void => {
                valid = validateLineItemAmount(lineItem, errors) && valid;
                valid = validateLineItemName(lineItem, errors) && valid;
            });
        });

        if (!valid) {
            return;
        }
        dispatch(loanPayoutFiguresAddAction(loanPayoutFigure));
    }, [
        appliedPrepaidInterestMode,
        sections,
        dispatch,
        loanPayoutFigure,
        validateDischargeDate,
        validatePrepaidInterest,
        validateLineItemAmount,
        validateLineItemName,
    ]);

    const onChangeAccruedInterestMode: (accruedInterestMode: LoanPayoutFigureAccruedInterestModeEnum) => void = useCallback((accruedInterestMode: LoanPayoutFigureAccruedInterestModeEnum): void => {
        setAccruedInterestMode(accruedInterestMode);
    }, []);

    const onChangeMinimumTermInterestMode: (minimumTermInterestMode: LoanPayoutFigureMinimumTermInterestModeEnum) => void = useCallback((minimumTermInterestMode: LoanPayoutFigureMinimumTermInterestModeEnum): void => {
        setMinimumTermInterestMode(minimumTermInterestMode);
    }, []);

    if (!fees || !loan || !transactions) {
        return (
            <Layout uuid={uuid} section='payout-figure'>
                <Spin />
            </Layout>
        );
    }

    if (!loan.application.dischargeInterestType) {
        return (
            <Layout uuid={uuid} section='payout-figure'>
                <p>Please set the discharge interest type</p>
                <Modal
                    destroyOnClose={true}
                    okText='Confirm'
                    onCancel={onClickMissingDetailsCancel}
                    onOk={onClickMissingDetailsOk}
                    open={isMissingDetailsModalOpen}
                    title='Missing Details'
                >
                    <Form.Item label='Discharge Interest Type' className='discharge-interest-type'>
                        <Select onChange={onChangeDischargeInterestType}>
                            <Select.Option value={DischargeInterestTypeEnum.BreakCost}>Break Cost</Select.Option>
                            <Select.Option value={DischargeInterestTypeEnum.MinimumTerm}>Minimum Term</Select.Option>
                        </Select>
                    </Form.Item>
                </Modal>
            </Layout>
        );
    }

    const { balanceAmount, totalPayoutFigure } = getLoanPayoutFigureTotals(loan, loanPayoutFigure);
    const appliedAccruedInterestTotal: number = getLoanAppliedAccruedInterestTotal(loanPayoutFigure);

    const loanIs360DayInterestMethod: boolean = InterestMethodEnum.InterestMethod360 === loan.interestMethod;

    // Loans without the current payment date (the most recent interest inclusive loan transaction date or loan start date) cannot be calculated.
    const canAutoCalculateInterest: boolean = !!loan.currentPaymentDate;

    const payoutFigureCalculation: ILoanPayoutFigureCalculation = loanPayoutFigureCalculations && loanPayoutFigureCalculations[dischargeDate];

    const prepaidInterestMenu: MenuProps = {
        items: [
            ...(appliedPrepaidInterestMode === LoanPayoutFigurePrepaidInterestModeEnum.Manual ? [
                {
                    key: 'auto-calculate',
                    label: 'Auto Calculate',
                    onClick: switchToPrepaidInterestAuto,
                },
            ] : []),
            ...(appliedPrepaidInterestMode === LoanPayoutFigurePrepaidInterestModeEnum.Auto ? [
                {
                    key: 'enter-amount',
                    label: 'Enter Amount',
                    onClick: switchToPrepaidInterestManual,
                },
            ] : []),
        ],
    };

    const prepaidInterestLabel: ReactNode = (
        <Dropdown menu={prepaidInterestMenu}>
            <a>Less Remaining Prepaid Interest <DownOutlined /></a>
        </Dropdown>
    );

    const prepaidInterestLabelBlock: ReactNode = canAutoCalculateInterest && loanIs360DayInterestMethod ? (
        <Form.Item className='dropdown-label' label={prepaidInterestLabel} />
    ) : (
        <span className='label'>Less Remaining Prepaid Interest</span>
    );

    const prepaidInterestAmountBlock: ReactNode = payoutFigureCalculation ? (
        <span>{currencyFormatter.format(payoutFigureCalculation.prepaidInterest)}</span>
    ) : (
        <Spin size='small' />
    );

    const prepaidInterestBlock: ReactNode = appliedPrepaidInterestMode === LoanPayoutFigurePrepaidInterestModeEnum.Auto ? (
        <Space className='amount'>
            {!dischargeDate ? <Tooltip overlay='Select a proposed payout date'><WarningOutlined /></Tooltip> : prepaidInterestAmountBlock}
        </Space>
    ) : (
        <Form.Item help={errors.prepaidInterest} validateStatus={errors.prepaidInterest && 'error'}>
            <InputNumber addonBefore='$' className='amount' onChange={onChangePrepaidInterest} value={prepaidInterest} />
        </Form.Item>
    );

    const accruedInterestAmountBlock: ReactNode = payoutFigureCalculation ? (
        <Space className='line-item'>
            <span className='label'>{payoutFigureCalculation.accruedInterestLabel}</span>
            <Space className='amount'>
                <span>{currencyFormatter.format(appliedAccruedInterestTotal)}</span>
            </Space>
        </Space>
    ) : (
        <Space direction='vertical'>
            <Spin size='small' />
        </Space>
    );

    const accruedInterestTotalBlock: ReactNode = dischargeDate && appliedAccruedInterestMode === LoanPayoutFigureAccruedInterestModeEnum.Auto && (
        <Space direction='vertical'>
            {accruedInterestAmountBlock}
        </Space>
    );

    const sectionsBlock: ReactNode[] = _.map(defaultSections, (defaultSection: LoanPayoutFigureSectionTypeEnum): ReactNode => {
        const section: ISection = _.find(sections, (loopSection: ILoanPayoutFigureSection): boolean => loopSection.type === defaultSection);

        if (!section) {
            const onClickAdd: () => void = (): void => onClickAddSection(defaultSection);

            return (
                <React.Fragment key={sectionLabels[defaultSection]}>
                    <Divider />
                    <Space direction='vertical'>
                        <Space>
                            <strong>{sectionLabels[defaultSection]}</strong>
                            {defaultSection !== LoanPayoutFigureSectionTypeEnum.Principal && (<Button onClick={onClickAdd} size='small' type='link'>Add</Button>)}
                        </Space>
                    </Space>
                </React.Fragment>
            );
        }

        const itemsBlock: ReactNode[] = _.map(section.lineItems, (lineItem: ILineItem): ReactNode => {
            if (section.type === LoanPayoutFigureSectionTypeEnum.Principal) {
                return (
                    <Space className='line-item' key={lineItem.code}>
                        <span className='label'>{lineItem.name}</span>
                        <span className='amount'>{currencyFormatter.format(lineItem.amount)}</span>
                    </Space>
                );
            }

            const onClickDelete: () => void = (): void => onClickDeleteLineItem(section.type, lineItem.code);
            const onChangeAmount: (value: number) => void = (value: number): void => onChangeLineItemAmount(section.type, lineItem.code, value);
            const onChangeName: (event: React.ChangeEvent<HTMLInputElement>) => void = (event: React.ChangeEvent<HTMLInputElement>): void => onChangeLineItemName(section.type, lineItem.code, event);

            return (
                <Space className='line-item' key={lineItem.code}>
                    <Form.Item help={errors[`lineItem_${lineItem.code}_name`]} validateStatus={errors[`lineItem_${lineItem.code}_name`] && 'error'}>
                        <Input className='label' onChange={onChangeName} value={lineItem.name} />
                    </Form.Item>
                    <Form.Item help={errors[`lineItem_${lineItem.code}_amount`]} validateStatus={errors[`lineItem_${lineItem.code}_amount`] && 'error'}>
                        <InputNumber addonBefore='$' className='amount' onChange={onChangeAmount} value={lineItem.amount} />
                    </Form.Item>
                    <Button onClick={onClickDelete} size='small' type='link'>Delete</Button>
                </Space>
            );
        });

        let principalSectionSummaryBlock: ReactNode;
        if (section.type === LoanPayoutFigureSectionTypeEnum.Principal) {
            const principalAmount: number = _.reduce(section.lineItems, (total: number, lineItem: ILineItem): number => total + lineItem.amount, 0);

            principalSectionSummaryBlock = (
                <Space className='line-item'>
                    <span className='label'><strong>Principal Balance</strong></span>
                    <span className='amount'><strong>{currencyFormatter.format(principalAmount)}</strong></span>
                </Space>
            );
        }

        const onClickDelete: () => void = (): void => onClickDeleteSection(section.type);
        const onClickAddItem: () => void = (): void => onClickAddLineItem(section.type);

        return (
            <React.Fragment key={section.type}>
                <Divider />
                <Space direction='vertical'>
                    <Space>
                        <strong>{section.name}</strong>
                        {[ LoanPayoutFigureSectionTypeEnum.DefaultFees, LoanPayoutFigureSectionTypeEnum.Renewal ].includes(section.type) && <Button onClick={onClickDelete} size='small' type='link'>Remove</Button>}
                        {section.type === LoanPayoutFigureSectionTypeEnum.Interest && canAutoCalculateInterest && loanIs360DayInterestMethod && <Button onClick={onClickAutoCalculate} size='small' type='link'>Auto Calculate</Button>}
                    </Space>
                    <React.Fragment>
                        {section.type === LoanPayoutFigureSectionTypeEnum.Interest && accruedInterestTotalBlock}
                        {itemsBlock}
                        {section.type === LoanPayoutFigureSectionTypeEnum.Principal && principalSectionSummaryBlock}
                    </React.Fragment>
                    {section.type !== LoanPayoutFigureSectionTypeEnum.Principal && (<Button onClick={onClickAddItem}>Add Item</Button>)}
                </Space>
            </React.Fragment>
        );
    });

    const loanCodeBlock: JSX.Element = loan.salesforceCode && loan.code !== loan.salesforceCode && (
        <Form.Item label='Loan ID'>
            <Radio.Group onChange={onChangeLoanCode} value={loanCode || loan.code}>
                <Radio value={loan.code}>{loan.code}</Radio>
                <Radio value={loan.salesforceCode}>{loan.salesforceCode}</Radio>
            </Radio.Group>
        </Form.Item>
    );

    const totalPayoutAmountBlock: ReactNode = payoutFigureCalculation ? (
        <span>{currencyFormatter.format(totalPayoutFigure)}</span>
    ) : (
        <Spin size='small' />
    );

    const totalPayoutBlock: ReactNode = (
        <Space className='amount'>
            <span className='amount'><strong>{!dischargeDate ? <Tooltip overlay='Select a proposed payout date'><WarningOutlined /></Tooltip> : totalPayoutAmountBlock}</strong></span>
        </Space>
    );

    const balanceDueAmountBlock: ReactNode = payoutFigureCalculation ? (
        <span>{currencyFormatter.format(balanceAmount)}</span>
    ) : (
        <Spin size='small' />
    );

    const balanceDueBlock: ReactNode = (
        <Space className='amount'>
            <span className='amount'><strong>{!dischargeDate ? <Tooltip overlay='Select a proposed payout date'><WarningOutlined /></Tooltip> : balanceDueAmountBlock}</strong></span>
        </Space>
    );

    return (
        <Layout uuid={uuid} section='payout-figure'>
            <Space className='actions'>
                <Button
                    className='create'
                    disabled={!payoutFigureCalculation}
                    onClick={onClickCreate}
                    type='primary'
                >
                    Create
                </Button>
            </Space>
            <Typography.Title level={2}>Payout Figure</Typography.Title>
            {loanCodeBlock}
            <Form.Item className='borrower-name' label='Borrower'>
                <Input onChange={onChangeBorrowerName} value={borrowerName || loan.contactName} />
            </Form.Item>
            <Form.Item className='discharge-date' help={errors.dischargeDate} label='Proposed Payout Date' validateStatus={errors.dischargeDate && 'error'}>
                <DatePicker
                    disabledDate={disabledDate}
                    onChange={onChangeDischargeDate}
                    format='DD/MM/YYYY'
                    value={dischargeDate ? dayjs(dischargeDate) : null}
                />
            </Form.Item>
            <React.Fragment>
                {sectionsBlock}
            </React.Fragment>
            <Divider />
            <Space direction='vertical'>
                <Space className='line-item'>
                    <span className='label'>Total Payout Figure</span>
                    {totalPayoutBlock}
                </Space>
                <Space className='line-item'>
                    {prepaidInterestLabelBlock}
                    {prepaidInterestBlock}
                </Space>
                <Space className='line-item'>
                    <span className='label'>Less Amount Held In Trust</span>
                    <Form.Item help={errors.trustAmount} validateStatus={errors.trustAmount && 'error'}>
                        <InputNumber addonBefore='$' className='amount' onChange={onChangeTrustAmount} value={trustAmount} />
                    </Form.Item>
                </Space>
                <Space className='line-item'>
                    <span className='label'><strong>Balance Due</strong></span>
                    {balanceDueBlock}
                </Space>
            </Space>
            <PayoutFigureInterestCalculatorModal
                isOpen={isInterestCalculatorModalOpen}
                onClose={onCloseInterestCalculatorModal}
                payoutFigure={loanPayoutFigure}
                onChangeAccruedInterestMode={onChangeAccruedInterestMode}
                onChangeMinimumTermInterestMode={onChangeMinimumTermInterestMode}
            />
        </Layout>
    );

}
